/**
 * Room Service for handling room-related API calls
 *
 * This service provides methods for fetching and manipulating room data
 * from the API, built on top of the base apiService.
 */

import { PaginatedResponse, Room, SearchFilters } from '../types';
import apiService from './api-service';

class RoomService {
    private baseUrl = '/v1/rooms';

    /**
     * Get a paginated list of rooms
     * @param filters - Optional search filters
     * @returns Promise with paginated room data
     */
    async getRooms(filters?: SearchFilters): Promise<PaginatedResponse<Room>> {
        const queryString = filters ? `?${apiService.buildQueryParams(filters)}` : '';
        return apiService.get<PaginatedResponse<Room>>(`${this.baseUrl}${queryString}`);
    }

    /**
     * Get a specific room by ID
     * @param id - Room ID
     * @returns Promise with room data
     */
    async getRoom(id: number): Promise<Room> {
        return apiService.get<Room>(`${this.baseUrl}/${id}`);
    }

    /**
     * Search for rooms by name or description
     * @param query - Search query string
     * @param filters - Optional additional filters
     * @returns Promise with paginated room data
     */
    async searchRooms(query: string, filters?: Omit<SearchFilters, 'query'>): Promise<PaginatedResponse<Room>> {
        const searchFilters: SearchFilters = { query, ...filters };
        const queryString = apiService.buildQueryParams(searchFilters);
        return apiService.get<PaginatedResponse<Room>>(`${this.baseUrl}/search?${queryString}`);
    }
}

// Create a singleton instance
const roomService = new RoomService();

export default roomService;
